import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';

import WorkflowStatusesTableEmpty from './WorkflowStatusesTableEmpty';
import WorkflowStatusesTableRow from './WorkflowStatusesTableRow';

const styles = theme => ({
  gridItem: {
    maxWidth: '20%',
    flexBasis: '20%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '33.33%',
      flexBasis: '33.33%',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '50%',
      flexBasis: '50%',
    },
  },
  table: {
    display: 'block',
    height: '100%',
  },
  tableBody: {
    display: 'block',
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 2px 10px 0 rgba(72,126,161,0.14)',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  tableHead: {
    display: 'block',
    width: '100%',
    position: 'sticky',
    zIndex: 40,
    top: 207,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: -8,
      right: -8,
      bottom: 0,
      zIndex: -1,
      backgroundColor: theme.palette.grey[100],
    },
    [theme.breakpoints.down('sm')]: {
      top: 170,
      zIndex: 20,
      transition: theme.transitions.create(['top'], {
        duration: theme.transitions.duration.short,
      }),
    },
  },
  tableHeadMobileFilterExpanded: {
    [theme.breakpoints.down('sm')]: {
      top: 215,
    },
  },
  cellIntake: {
    color: theme.palette.states.intake,
  },
  cellDelivery: {
    color: theme.palette.states.delivery,
  },
  cellBilling: {
    color: theme.palette.states.billing,
  },
  cellCollection: {
    color: theme.palette.states.collection,
  },
  cellPaid: {
    color: theme.palette.states.paid,
  },
  cellHidden: {
    display: 'none',
    fontSize: 0,
  },
  tableRow: {
    display: 'flex',
    width: '100%',
    flexWrap: 'nowrap',
  },
  cellName: {
    display: 'flex',
    maxWidth: '100%',
    flexBasis: '100%',
    paddingTop: 8,
    paddingBottom: 8,
    alignItems: 'center',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
});

const WorkflowStatusesTable = ({
  onWorkflowStatusDeleteFail,
  items,
  classes,
  loading,
  mobileFilterExpanded,
}) => {
  if (loading === 0 && items.length === 0) {
    return (<WorkflowStatusesTableEmpty />);
  } else if (items.length === 0) {
    return '';
  }
  const intakeItems = items.filter(item => item['Workflow State'].ID === 1);
  const deliveryItems = items.filter(item => item['Workflow State'].ID === 2);
  const billingItems = items.filter(item => item['Workflow State'].ID === 3);
  const collectionItems = items.filter(item => item['Workflow State'].ID === 4);
  const paidItems = items.filter(item => item['Workflow State'].ID === 5);
  return (
    <Grid
      container
      spacing={24}
    >
      <Grid
        className={classes.gridItem}
        item
        xs={3}
      >
        <Table className={classes.table}>
          <TableHead
            className={classNames(classes.tableHead, {
              [classes.tableHeadMobileFilterExpanded]: mobileFilterExpanded,
            })}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellIntake, classes.cellName)}>Intake</TableCell>
              <TableCell className={classes.cellHidden}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {intakeItems.map(item => (
              <WorkflowStatusesTableRow
                key={item.ID}
                onWorkflowStatusDeleteFail={onWorkflowStatusDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        className={classes.gridItem}
        item
        xs={3}
      >
        <Table className={classes.table}>
          <TableHead
            className={classNames(classes.tableHead, {
              [classes.tableHeadMobileFilterExpanded]: mobileFilterExpanded,
            })}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellDelivery, classes.cellName)}>Delivery</TableCell>
              <TableCell className={classes.cellHidden}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {deliveryItems.map(item => (
              <WorkflowStatusesTableRow
                key={item.ID}
                onWorkflowStatusDeleteFail={onWorkflowStatusDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        className={classes.gridItem}
        item
        xs={3}
      >
        <Table className={classes.table}>
          <TableHead
            className={classNames(classes.tableHead, {
              [classes.tableHeadMobileFilterExpanded]: mobileFilterExpanded,
            })}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellBilling, classes.cellName)}>Billing</TableCell>
              <TableCell className={classes.cellHidden}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {billingItems.map(item => (
              <WorkflowStatusesTableRow
                key={item.ID}
                onWorkflowStatusDeleteFail={onWorkflowStatusDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        className={classes.gridItem}
        item
        xs={3}
      >
        <Table className={classes.table}>
          <TableHead
            className={classNames(classes.tableHead, {
              [classes.tableHeadMobileFilterExpanded]: mobileFilterExpanded,
            })}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellCollection, classes.cellName)}>Collection</TableCell>
              <TableCell className={classes.cellHidden}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {collectionItems.map(item => (
              <WorkflowStatusesTableRow
                key={item.ID}
                onWorkflowStatusDeleteFail={onWorkflowStatusDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        className={classes.gridItem}
        item
        xs={3}
      >
        <Table className={classes.table}>
          <TableHead
            className={classNames(classes.tableHead, {
              [classes.tableHeadMobileFilterExpanded]: mobileFilterExpanded,
            })}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellPaid, classes.cellName)}>Paid</TableCell>
              <TableCell className={classes.cellHidden}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {paidItems.map(item => (
              <WorkflowStatusesTableRow
                key={item.ID}
                onWorkflowStatusDeleteFail={onWorkflowStatusDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};

WorkflowStatusesTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  loading: PropTypes.number,
  onWorkflowStatusDeleteFail: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    table: PropTypes.string.isRequired,
    cellIntake: PropTypes.string.isRequired,
  }).isRequired,
};

WorkflowStatusesTable.defaultProps = {
  loading: 0,
};

export default withStyles(styles)(WorkflowStatusesTable);



// WEBPACK FOOTER //
// ./src/components/settings/WorkflowStatusesTable/WorkflowStatusesTable.js